import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { filter, Observable } from 'rxjs';
import { catchError, concatMap, map, retryWhen, share, switchMap, tap } from 'rxjs/operators';
import { UserState } from '../../shared/user-state/user.state';
import { AlertService } from './alert.service';
import {
  BandmanagerRestApiAuthenticationService,
  BandmanagerRestApiTokensResponseDto
} from '@digitale-menschen/bandmanager-rest-api';
import { DeviceState } from '../../shared/device-state/device.state';
import { UserStateActions } from '../../shared/user-state/user-state.actions';
import { LoadingService } from "./loading.service";

@Injectable()
export class Interceptor implements HttpInterceptor {
  private alertService = inject(AlertService);
  private store = inject(Store);
  private loadingService = inject(LoadingService);
  private apiAuthenticationService = inject(BandmanagerRestApiAuthenticationService);

  private retryRequest = Symbol('reload');
  private refreshToken = this.store.select(UserState.refreshToken)
    .pipe(
      filter(refreshToken => refreshToken !== null),
      concatMap(refreshToken => this.apiAuthenticationService
        .authenticationControllerRefreshToken({
          body: {
            refreshToken,
            deviceId: this.store.selectSnapshot(DeviceState.deviceInfos)?.uniqueIdentifier || 'x',
          },
        })
        .pipe(
          map(response => {
            try {
              return JSON.parse(response as any);
            } catch (noNewTokensException) {
              return response;
            }
          }),
          tap((newTokenResponse: BandmanagerRestApiTokensResponseDto) => {
            if (newTokenResponse.access_token && newTokenResponse.refresh_token) {
              this.store.dispatch(new UserStateActions.SetRefreshToken(newTokenResponse.refresh_token));
              this.store.dispatch(new UserStateActions.SetAuthToken(newTokenResponse.access_token));
              throw this.retryRequest;
            }
          }),
          share(),
        ) as Observable<any>),
    );

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const request$ = new Observable<HttpRequest<any>>(observer => {
      observer.next(this.addHeaders(request));
      observer.complete();
    });

    return request$.pipe(
      switchMap(req => next.handle(req)),
      catchError((error: Error) => {
        this.loadingService.hide();
        if (error instanceof HttpErrorResponse) {
          this.loadingService.hide();
          // Catch refresh token error, which does a logout.
          if (error.url && error.url.indexOf(BandmanagerRestApiAuthenticationService.AuthenticationControllerRefreshTokenPath) > -1) {
            this.store.dispatch(new UserStateActions.SetIsLoggedIn(false));
            throw error;
          }
          switch (error.status) {
            case 0:
            case -1:
              this.alertService.display('error', 'OFFLINE');
              throw error;
            case 401:
              return this.refreshToken;
            default:
              const errorText: string = error.error?.message || error.statusText;
              this.alertService.display('error', errorText);
              throw error;
          }
        } else {
          throw error;
        }
      }),
      retryWhen(err$ =>
        err$.pipe(
          tap(err => {
            if (err === this.retryRequest) {
              return;
            }
            throw err;
          }),
        ),
      ),
    );
  }

  /**
   * Adds the bearer token to the http request.
   *
   * @param req
   * @private
   */
  private addHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const activeLanguage = this.store.selectSnapshot(UserState.preferencesLanguage) || 'en';
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${this.store.selectSnapshot(UserState.accessToken)}`,
        'Accept-Language': `${activeLanguage};q=0.9,en-US,en;q=0.8`,
      },
    });
  }
}
